import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import moment from 'moment';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import Notify from '../../../../../shared/components/widgets/Notify';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';

export default function DspLandingPages() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [forceRefresh, setForceRefresh] = useState(moment());
  const [agGrid, setAgGrid] = useState(null);
  const [updatedRowCount, setUpdatedRowCount] = useState(0);
  const [originalData, setOriginalData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // function jsonCellRenderer(params) {
  // // Hold  cellRenderer: (params) => <pre style={{ fontFamily: 'monospace', margin: 0, padding: 0, whiteSpace: 'pre-wrap' }}>${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>
  //   return `<pre style="font-family: monospace; margin: 0; padding: 0; white-space: pre-wrap;">${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>`;
  // }

  const resetRow = (orgRowObj) => {
    agGrid.gridApi.forEachNode((rowNode) => {
      if (orgRowObj.landingPageId === rowNode.data.landingPageId) {
        rowNode.setData(orgRowObj);
        setUpdatedRowCount(curentCount => curentCount - 1);
      }
    });
  };

  const removeRow = (rowData) => {
    agGrid.gridApi.applyTransaction({ remove: [rowData.data] });
    setUpdatedRowCount(curentCount => curentCount - 1);
  };

  const submitDspLP = () => {
    setSubmitting(true);
    const dspLandingPageUrl = `${config.serverUrl}/management/dsp/landing-pages`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const saveData = [];
    // Build changes
    agGrid.gridApi.forEachNode((rowNode) => {
      if (rowNode.data.change || rowNode.data.landingPageId === 0) {
        const { change, ...rest } = rowNode.data;
        Object.entries(rest).forEach(([key, value]) => {
          if (key === 'budgetAmount' && (typeof value !== 'number' || Number.isNaN(value))) {
            rest[key] = parseFloat(value || 0.0);
          }
        });
        saveData.push({ ...rest }); // Remove fields we don't care about and add to changedRows
      }
    });

    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId: selectedSeller.value,
        saveData,
      }),
    };

    fetch(dspLandingPageUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then(() => {
        Notify.show('primary', 'Success', 'Landing Pages have been saved!');
        setUpdatedRowCount(0);
        setForceRefresh(moment());
        setSubmitting(false);
      }).catch(() => {
        Notify.show('danger', 'Error', 'We were unable to save your Landing Pages.');
        setForceRefresh(moment());
        setSubmitting(false);
      });
  };

  const highlightChanges = (row) => {
    if ((row.data?.change && row.value !== row.data.change[row.colDef.field]) || (row.data.landingPageId === 0)) {
      return { backgroundColor: '#91BE23' };
    }
    return { backgroundColor: '' };
  }

  const columnDefs = [
    {
      field: 'change',
      headerName: 'CHANGE',
      cellStyle: (params) => (params.value !== '' ? { color: '#91BE23' } : { color: 'black' }),
      cellRenderer: (params) => {
        if (params.value && params.data.landingPageId > 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => resetRow(params.value)}>Reset Row</button>;
        }
        if (params.data.landingPageId === 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => removeRow(params)}>Remove Row</button>;
        }
        return ''
      },
      editable: false,
    },
    { field: 'landingPageId', headerName: 'Landing Page ID', editable: false },
    { field: 'accountId', headerName: 'Account ID', editable: false },
    { field: 'budgetGroupName', headerName: 'Budget Group Name', cellStyle: params => highlightChanges(params) },
    { field: 'budgetAmount', headerName: 'Budget Amount', cellStyle: params => highlightChanges(params) },
    { field: 'startDate', headerName: 'Start Date', cellStyle: params => highlightChanges(params) },
    { field: 'endDate', headerName: 'End Date', cellStyle: params => highlightChanges(params) },
    { field: 'landingPage', headerName: 'Landing Page', cellStyle: params => highlightChanges(params) },
    { field: 'mlpId', headerName: 'MLP ID', cellStyle: params => highlightChanges(params) },
    { field: 'mlpType', headerName: 'MLP Type', cellStyle: params => highlightChanges(params) },
    { field: 'mlpName', headerName: 'MLP Name', cellStyle: params => highlightChanges(params) },
    { field: 'mlpUrl', headerName: 'MLP URL ', cellStyle: params => highlightChanges(params) },
    { field: 'destionationType', headerName: 'Destionation Type', cellStyle: params => highlightChanges(params) },
    { field: 'goalCostKpi', headerName: 'Goal Cost KPI', cellStyle: params => highlightChanges(params) },
    { field: 'goalConversionKpi', headerName: 'Goal Conversion KPI', cellStyle: params => highlightChanges(params) },
    { field: 'goalTactic', headerName: 'Goal Tactic', cellStyle: params => highlightChanges(params) },
    { field: 'targetConfigJson', headerName: 'Target Config []', cellStyle: params => highlightChanges(params) },
    { field: 'targetDtlsJson', headerName: 'Target Details {}', cellStyle: params => highlightChanges(params) },
    { field: 'gender', headerName: 'Gender', cellStyle: params => highlightChanges(params) },
    { field: 'audienceCustomIcpCsv', headerName: 'Custom ICP CSV', cellStyle: params => highlightChanges(params) },
    { field: 'locationCsv', headerName: 'Location CSV', cellStyle: params => highlightChanges(params) },
    { field: 'inventoryScope', headerName: 'Inventory Scope', cellStyle: params => highlightChanges(params) },
    { field: 'trackedAsinsJson', headerName: 'Tracked ASINs []', cellStyle: params => highlightChanges(params) },
    { field: 'categoryCsv', headerName: 'Category CSV', cellStyle: params => highlightChanges(params) },
    { field: 'mediaTypeCsv', headerName: 'Media Type CSV', cellStyle: params => highlightChanges(params) },
    { field: 'mediaPlanId', headerName: 'Media Plan ID', cellStyle: params => highlightChanges(params) },
    { field: 'targetState', headerName: 'Target State', cellStyle: params => highlightChanges(params) },
    { field: 'creativeState', headerName: 'Creative State', cellStyle: params => highlightChanges(params) },
    { field: 'eventState', headerName: 'EventState', cellStyle: params => highlightChanges(params) },
    { field: 'state', headerName: 'State', cellStyle: params => highlightChanges(params) },
    { field: 'updateTs', headerName: 'Update TS', editable: false },
    { field: 'createTs', headerName: 'Create TS', editable: false },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: true,
  };

  const showDatePicker = false;
  const showExportOptions = true;
  const apiEndPoint = `${config.serverUrl}/management/dsp/landing-pages`;

  const exportPrefix = getExportPrefix('amz-dsp-landing-pages', selectedSeller, true);

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  const parentSetData = (data) => {
    setOriginalData(JSON.parse(JSON.stringify(data)));
  }

  const onRowValueChanged = (cellData) => {
    if (!agGrid) {
      return;
    }
    agGrid.gridApi.forEachNode((rowNode) => {
      if (cellData.data.landingPageId === rowNode.data.landingPageId) {
        if (!rowNode.data.change) {
          const origData = originalData.find(origRow => origRow.landingPageId === cellData.data.landingPageId);
          rowNode.setDataValue('change', { ...origData });
          if (cellData.data.landingPageId !== 0) {
            setUpdatedRowCount(curentCount => curentCount + 1);
          }
        }
      }
    })
  }

  const getNewRow = () => {
    const newData = {
      landingPageId: 0,
      accountId: selectedSeller.value,
      budgetGroupName: null,
      budgetAmount: null,
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      landingPage: null,
      mlpId: null,
      mlpType: null,
      mlpName: null,
      mlpUrl: null,
      destionationType: null,
      goalCostKpi: null,
      goalConversionKpi: null,
      goalTactic: null,
      targetCostDriver: null,
      targetCostValue: null,
      targetConfigJson: null,
      targetDtlsJson: null,
      gender: null,
      audienceCustomIcpCsv: null,
      locationCsv: null,
      inventoryScope: null,
      trackedAsinsJson: null,
      categoryCsv: null,
      mediaTypeCsv: null,
      mediaPlanId: null,
      targetState: null,
      creativeState: null,
      eventState: null,
      state: null,
      updateTs: null,
      createTs: null,
    };
    setUpdatedRowCount(curentCount => curentCount + 1);
    return newData;
  }


  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
    }
  }, [forceRefresh]);

  return (
    <Container>
      <Row>
        <TableBuilder
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          apiEndPoint={apiEndPoint}
          showDatePicker={showDatePicker}
          showExportOptions={showExportOptions}
          exportPrefix={exportPrefix}
          parentAccess={parentAccess}
          parentSetData={parentSetData}
          onRowValueChanged={onRowValueChanged}
          showAddRowButton
          getNewRow={getNewRow}
        />
      </Row>
      <Row>
        <CardPanel
          title={`Updated Count: ${updatedRowCount}`}
          autorenewButton={false}
          closeButton={false}
        >
          <div style={{ width: '500px' }}>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitDspLP()}>Save Landing Pages</button>
          </div>
        </CardPanel>
      </Row>
    </Container>
  );
}
